import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Calendar, DollarSign, Film, Loader2, Theater, Ticket } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import {
  getPartnerBusinessType,
  isCinemaPartner,
  isStudioPartner,
} from '@/lib/partnerType';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';

type MarketplaceWork = {
  id: string;
  title: string;
  poster_url: string | null;
  status: string | null;
  theatrical_listed: boolean;
  theatrical_ticket_price_usd: number | null;
  theatrical_public_release_date: string | null;
  publisher_id: string | null;
};

type TheaterRelease = {
  id: string;
  release_date: string;
  ticket_price_usd: number;
  public_release_date: string | null;
  status: string;
  work_id: string;
  works: { title: string; poster_url: string | null } | null;
};

function formatUsd(value: number | null): string {
  if (value == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function CinemaGatePrompt() {
  return (
    <Card>
      <CardHeader>
        <CardTitle className='flex items-center gap-2'>
          <Theater className='h-5 w-5 text-theatre-300' />
          Complete cinema onboarding
        </CardTitle>
        <CardDescription>
          Sign the cinema exhibition agreement to browse publisher-priced titles and book
          screenings.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button asChild>
          <Link to='/onboarding?step=contract'>Continue onboarding</Link>
        </Button>
      </CardContent>
    </Card>
  );
}

function StudioListingPanel({ publisherId }: { publisherId: string }) {
  const [works, setWorks] = useState<MarketplaceWork[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedWorkId, setSelectedWorkId] = useState<string>('');
  const [listed, setListed] = useState(false);
  const [price, setPrice] = useState('');
  const [releaseDate, setReleaseDate] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('works')
        .select(
          'id, title, poster_url, status, theatrical_listed, theatrical_ticket_price_usd, theatrical_public_release_date, publisher_id'
        )
        .eq('publisher_id', publisherId)
        .neq('status', 'archived')
        .order('title');
      if (error) throw error;
      setWorks((data ?? []) as MarketplaceWork[]);
    } catch (error) {
      console.warn('Failed to load works:', error);
      toast.error('Failed to load your titles');
    } finally {
      setLoading(false);
    }
  }, [publisherId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const work = works.find(w => w.id === selectedWorkId);
    if (!work) {
      setListed(false);
      setPrice('');
      setReleaseDate('');
      return;
    }
    setListed(work.theatrical_listed);
    setPrice(work.theatrical_ticket_price_usd?.toString() ?? '');
    setReleaseDate(work.theatrical_public_release_date ?? '');
  }, [selectedWorkId, works]);

  const saveListing = async () => {
    if (!selectedWorkId) return;
    const parsedPrice = Number(price);
    if (listed && (!Number.isFinite(parsedPrice) || parsedPrice <= 0 || !releaseDate)) {
      toast.error('Set a ticket price and streaming unlock date to list on the marketplace');
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from('works')
        .update({
          theatrical_listed: listed,
          theatrical_ticket_price_usd: listed ? parsedPrice : null,
          theatrical_public_release_date: listed ? releaseDate : null,
        })
        .eq('id', selectedWorkId);
      if (error) throw error;
      toast.success(listed ? 'Title listed on Theatre Marketplace' : 'Theatrical listing removed');
      load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update listing');
    } finally {
      setSaving(false);
    }
  };

  const listedWorks = works.filter(w => w.theatrical_listed);

  return (
    <div className='space-y-6'>
      <Card>
        <CardHeader>
          <CardTitle>List a title for theatrical booking</CardTitle>
          <CardDescription>
            Set your ticket price and streaming unlock date before streaming goes live. Cinema
            partners book at your price; streaming unlocks after the theatrical window.
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          {loading ? (
            <Skeleton className='h-24' />
          ) : (
            <>
              <div className='space-y-2'>
                <Label>Title</Label>
                <Select value={selectedWorkId} onValueChange={setSelectedWorkId}>
                  <SelectTrigger>
                    <SelectValue placeholder='Select a work' />
                  </SelectTrigger>
                  <SelectContent>
                    {works.map(work => (
                      <SelectItem key={work.id} value={work.id}>
                        {work.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedWorkId && (
                <>
                  <label className='flex items-center gap-2 text-sm'>
                    <Checkbox checked={listed} onCheckedChange={v => setListed(v === true)} />
                    List on Theatre Marketplace
                  </label>
                  <div className='grid gap-4 sm:grid-cols-2'>
                    <div className='space-y-2'>
                      <Label htmlFor='ticket-price'>Ticket price (USD)</Label>
                      <Input
                        id='ticket-price'
                        type='number'
                        min='0'
                        step='0.01'
                        value={price}
                        onChange={e => setPrice(e.target.value)}
                        disabled={!listed}
                      />
                    </div>
                    <div className='space-y-2'>
                      <Label htmlFor='streaming-unlock'>Streaming unlock date</Label>
                      <Input
                        id='streaming-unlock'
                        type='date'
                        value={releaseDate}
                        onChange={e => setReleaseDate(e.target.value)}
                        disabled={!listed}
                      />
                    </div>
                  </div>
                  <Button onClick={saveListing} disabled={saving}>
                    {saving ? (
                      <>
                        <Loader2 className='h-4 w-4 animate-spin' />
                        Saving…
                      </>
                    ) : (
                      'Save listing'
                    )}
                  </Button>
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Active marketplace listings</CardTitle>
          <CardDescription>Titles cinema partners can browse and book.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Skeleton className='h-16' />
          ) : listedWorks.length === 0 ? (
            <p className='text-sm text-muted-foreground'>No titles listed yet.</p>
          ) : (
            <div className='divide-y divide-border'>
              {listedWorks.map(work => (
                <div key={work.id} className='flex items-center gap-3 py-3'>
                  <Film className='h-4 w-4 text-muted-foreground shrink-0' />
                  <div className='min-w-0 flex-1'>
                    <p className='truncate font-medium text-sm'>{work.title}</p>
                    <p className='text-xs text-muted-foreground'>
                      {formatUsd(work.theatrical_ticket_price_usd)} · streaming unlock{' '}
                      {formatDate(work.theatrical_public_release_date)}
                    </p>
                  </div>
                  <Badge variant='secondary'>Listed</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function CinemaBrowsePanel() {
  const [catalog, setCatalog] = useState<MarketplaceWork[]>([]);
  const [bookings, setBookings] = useState<TheaterRelease[]>([]);
  const [loading, setLoading] = useState(true);
  const [bookWork, setBookWork] = useState<MarketplaceWork | null>(null);
  const [screeningDate, setScreeningDate] = useState('');
  const [booking, setBooking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: catalogData, error: catalogError } = await supabase
        .from('works')
        .select(
          'id, title, poster_url, status, theatrical_listed, theatrical_ticket_price_usd, theatrical_public_release_date, publisher_id'
        )
        .eq('theatrical_listed', true)
        .order('title');
      if (catalogError) throw catalogError;
      setCatalog((catalogData ?? []) as MarketplaceWork[]);

      const { data: bookingData, error: bookingError } = await supabase
        .from('theater_releases')
        .select(
          'id, release_date, ticket_price_usd, public_release_date, status, work_id, works(title, poster_url)'
        )
        .order('release_date', { ascending: false });
      if (bookingError) throw bookingError;
      setBookings((bookingData ?? []) as unknown as TheaterRelease[]);
    } catch (error) {
      console.warn('Marketplace load failed:', error);
      toast.error('Failed to load marketplace');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const confirmBooking = async () => {
    if (!bookWork || !screeningDate) return;
    setBooking(true);
    try {
      const { data, error } = await supabase.rpc('book_theatrical_screening', {
        p_work_id: bookWork.id,
        p_release_date: screeningDate,
      });
      if (error) throw error;
      toast.success(`Screening booked — release ${data}`);
      setBookWork(null);
      setScreeningDate('');
      load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Booking failed');
    } finally {
      setBooking(false);
    }
  };

  return (
    <div className='space-y-6'>
      <Card>
        <CardHeader>
          <CardTitle>Publisher catalog</CardTitle>
          <CardDescription>
            Browse titles with publisher-set ticket prices and book screening dates for your cinema.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Skeleton className='h-24' />
          ) : catalog.length === 0 ? (
            <p className='text-sm text-muted-foreground'>No titles listed on the marketplace yet.</p>
          ) : (
            <div className='divide-y divide-border'>
              {catalog.map(work => (
                <div key={work.id} className='flex flex-wrap items-center gap-3 py-3'>
                  <Ticket className='h-4 w-4 text-theatre-300 shrink-0' />
                  <div className='min-w-0 flex-1'>
                    <p className='font-medium text-sm'>{work.title}</p>
                    <p className='text-xs text-muted-foreground'>
                      {formatUsd(work.theatrical_ticket_price_usd)} · streaming unlock{' '}
                      {formatDate(work.theatrical_public_release_date)}
                    </p>
                  </div>
                  <Button size='sm' onClick={() => setBookWork(work)}>
                    <Calendar className='h-4 w-4' />
                    Book screening
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Your bookings</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Skeleton className='h-16' />
          ) : bookings.length === 0 ? (
            <p className='text-sm text-muted-foreground'>No screenings booked yet.</p>
          ) : (
            <div className='divide-y divide-border'>
              {bookings.map(release => (
                <div key={release.id} className='flex items-center gap-3 py-3'>
                  <DollarSign className='h-4 w-4 text-muted-foreground shrink-0' />
                  <div className='min-w-0 flex-1'>
                    <p className='truncate text-sm font-medium'>
                      {release.works?.title ?? 'Untitled work'}
                    </p>
                    <p className='text-xs text-muted-foreground'>
                      Screening {formatDate(release.release_date)} ·{' '}
                      {formatUsd(release.ticket_price_usd)} · streaming unlock{' '}
                      {formatDate(release.public_release_date)}
                    </p>
                  </div>
                  <Badge variant='outline'>{release.status}</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!bookWork} onOpenChange={open => !open && setBookWork(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Book screening</DialogTitle>
            <DialogDescription>
              {bookWork?.title} — {formatUsd(bookWork?.theatrical_ticket_price_usd ?? null)} per
              ticket (publisher-set). Streaming unlocks{' '}
              {formatDate(bookWork?.theatrical_public_release_date ?? null)}.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-2'>
            <Label htmlFor='screening-date'>Screening date</Label>
            <Input
              id='screening-date'
              type='date'
              value={screeningDate}
              onChange={e => setScreeningDate(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setBookWork(null)}>
              Cancel
            </Button>
            <Button onClick={confirmBooking} disabled={booking || !screeningDate}>
              {booking ? 'Booking…' : 'Confirm booking'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function TheatreMarketplace() {
  const { publisher } = useAuth();
  const businessType = getPartnerBusinessType(publisher?.business_type, publisher?.partner_type);

  if (!publisher) return null;

  const pageTitle = isCinemaPartner(businessType) ? 'Book Screenings' : 'Theatre Marketplace';
  const pageDescription = isCinemaPartner(businessType)
    ? 'Browse publisher-priced catalog and schedule theatrical screenings'
    : 'List titles for cinema booking before streaming goes live';

  return (
    <div className='flex flex-col gap-6 p-4 md:p-6 text-foreground'>
      <div>
        <h1 className='text-2xl md:text-3xl font-bold'>{pageTitle}</h1>
        <p className='text-muted-foreground text-sm md:text-base'>{pageDescription}</p>
      </div>

      {isCinemaPartner(businessType) && !publisher.contract_signed && <CinemaGatePrompt />}

      {isCinemaPartner(businessType) && publisher.contract_signed && <CinemaBrowsePanel />}

      {isStudioPartner(businessType) && <StudioListingPanel publisherId={publisher.id} />}

      {!isCinemaPartner(businessType) && !isStudioPartner(businessType) && (
        <Card>
          <CardContent className='py-8 text-center text-sm text-muted-foreground'>
            Theatre Marketplace listing and booking is available to production studios and cinema
            partners.
          </CardContent>
        </Card>
      )}
    </div>
  );
}
